/**
 * Tablero del jefe de unidad (`GET /dashboards/jefe`). El ciclo de revision se detiene en la unidad hasta que esta
 * RESPONDE cada observacion, asi que la pieza central es la lista de observaciones por responder, con su texto y la
 * opcion de contestar ahi mismo. Luego estan las unidades (estado, avance y que les falta para completarse) y los
 * borradores por terminar. Lo ven tambien colaboradores y consulta; solo quien puede responder ve el boton.
 * Un administrador (que revisa, no responde) ve la lista sin el boton, igual que en la ficha de la actividad.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACTIVITY_STATUS_LABEL, type ActivityStatus, type UnitRatStatus } from '@rat/shared';
import { useAuth } from '../auth/AuthProvider';
import { canIn, hasAnywhere } from '../auth/access';
import { fetchJefeDashboard, respondObservation, type PendingObservation } from '../lib/queries';
import { describeGate } from '../lib/errors';
import { countBySeverity, waitingLabel } from '../lib/jefe';
import { PageHeader, ProgressBar, QueryState, UnitStatusPill } from '../components/ui';
import { Kpi } from '../components/DashboardWidgets';
import { fieldLabel } from '../components/Observations';

export function JefeDashboard() {
  const { access } = useAuth();
  const q = useQuery({ queryKey: ['jefe', 'dashboard'], queryFn: fetchJefeDashboard });
  const d = q.data;
  const pending = d ? countBySeverity(d.responder) : { obligatorias: 0, sugeridas: 0 };
  // Igual que ActivityDetail: responde la unidad; quien puede confirmar/resolver (revisor, administrador) no se contesta a si mismo.
  const canRespondHere = (o: PendingObservation) => canIn(access, 'observation.respond', o.unit_id) && !hasAnywhere(access, 'observation.resolve');

  return (
    <>
      <PageHeader
        eyebrow="Mi trabajo"
        title="Tablero del jefe de unidad"
        sub="Lo que la unidad tiene pendiente: primero, las observaciones del revisor que hay que responder"
      />
      <QueryState isLoading={q.isLoading} error={q.error}>
        {d && (
          <>
            <div className="kpis" style={{ marginBottom: 18 }}>
              <Kpi n={d.responder.length} l="Observaciones por responder" tone={pending.obligatorias > 0 ? 'critical' : undefined} />
              <Kpi
                n={pending.obligatorias}
                l="Obligatorias"
                title="Sin responderlas no se puede reenviar la actividad a revisión"
                tone={pending.obligatorias > 0 ? 'warning' : undefined}
              />
              <Kpi n={d.borradores.length} l="Borradores por completar" />
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card__b">
                <div className="eyebrow">Observaciones por responder</div>
                <p className="muted" style={{ fontSize: 12, margin: '2px 0 8px' }}>
                  Al responder, el revisor recibe su respuesta y la confirma o la reabre. Las obligatorias bloquean el reenvío de la actividad.
                </p>
                {d.responder.length === 0 ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>
                    No tiene observaciones por responder.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {d.responder.map((o) => (
                      <PendingItem key={o.observation_id} o={o} canRespond={canRespondHere(o)} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card__b">
                <div className="eyebrow">{d.unidades.length === 1 ? 'Mi unidad' : 'Mis unidades'}</div>
                {d.unidades.length === 0 ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>
                    No tiene unidades asignadas.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                    {d.unidades.map((u) => (
                      <div key={u.unit_id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Link to={`/unidad/${u.unit_id}`} style={{ fontWeight: 600 }}>
                            {u.name}
                          </Link>
                          <UnitStatusPill status={u.rat_status as UnitRatStatus} />
                        </div>
                        <div style={{ margin: '8px 0' }}>
                          <ProgressBar pct={u.avance_pct} />
                        </div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {Object.entries(u.por_estado)
                            .map(([s, n]) => `${ACTIVITY_STATUS_LABEL[s as ActivityStatus] ?? s}: ${n}`)
                            .join(' · ') || 'Sin actividades'}
                        </div>
                        <p style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                          <strong>Para completar la unidad: </strong>
                          {u.bloqueos_para_completar ? describeGate(u.bloqueos_para_completar) : 'todas sus actividades están aprobadas.'}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card__b">
                <div className="eyebrow">Borradores por completar</div>
                {d.borradores.length === 0 ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>
                    No hay borradores pendientes.
                  </p>
                ) : (
                  <div className="tablewrap" style={{ border: 0 }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Actividad</th>
                          <th style={{ width: 180 }}>Avance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.borradores.map((b) => (
                          <tr key={b.activity_id}>
                            <td className="mono">{b.ref_code ?? '—'}</td>
                            <td>
                              <Link to={`/actividades/${b.activity_id}`}>{b.title}</Link>
                            </td>
                            <td>
                              <ProgressBar pct={b.completeness_pct} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  );
}

/** Una observacion por responder, con su texto y la respuesta en linea. */
function PendingItem({ o, canRespond }: { o: PendingObservation; canRespond: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const respond = useMutation({
    mutationFn: (body: string) => respondObservation(o.observation_id, body),
    onSuccess: () => {
      // la fila sale de la lista (queda 'respondida'); la ficha de la actividad y su hilo tambien cambian
      void qc.invalidateQueries({ queryKey: ['jefe'] });
      void qc.invalidateQueries({ queryKey: ['activity'] });
    },
  });
  const obligatory = o.severity === 'obligatoria';

  return (
    <div
      style={{
        border: `1px solid ${obligatory ? 'var(--warning)' : 'var(--border)'}`,
        borderRadius: 8,
        padding: 10,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="mono">{o.ref_code ?? '—'}</span>
        <Link to={`/actividades/${o.activity_id}`} style={{ fontWeight: 600 }}>
          {o.title}
        </Link>
        <span className={`pill ${obligatory ? 'pill--warning' : 'pill--neutral'}`}>{obligatory ? 'Obligatoria' : 'Sugerida'}</span>
        {o.state === 'reabierta' && <span className="pill">Reabierta por el revisor</span>}
        <span style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 12 }}>
          {fieldLabel(o.field_path)}
          {o.created_at ? ` · ${waitingLabel(o.created_at)}` : ''}
        </span>
      </div>
      {o.text && <p style={{ margin: '6px 0 0', fontSize: 13 }}>{o.text}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        {canRespond && !open && (
          <button className="btn btn--primary" onClick={() => setOpen(true)}>
            Responder
          </button>
        )}
        <Link className="btn btn--ghost" to={`/actividades/${o.activity_id}`}>
          Abrir actividad
        </Link>
      </div>

      {canRespond && open && (
        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
          <textarea
            rows={3}
            placeholder="Explique qué corrigió o por qué no corresponde"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            style={{ resize: 'vertical', padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--ground)', color: 'var(--ink)', font: 'inherit' }}
          />
          {respond.error && <div className="callout callout--crit">{(respond.error as Error).message}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="btn btn--primary"
              disabled={reply.trim().length < 3 || respond.isPending}
              onClick={() => respond.mutate(reply.trim())}
            >
              {respond.isPending ? 'Enviando…' : 'Enviar respuesta'}
            </button>
            <button
              className="btn btn--ghost"
              disabled={respond.isPending}
              onClick={() => {
                setOpen(false);
                setReply('');
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
